<template>
  <div class="time-table-page">
    <div class="filter-header">
      <van-cell title="上课日期" :value="date" @click="showCalendar = true" />
      <van-icon name="filter-o" @click="showFilter = true" />
    </div>

    <van-calendar :show="showCalendar" :show-confirm="false" position="top" @select="onSelectDate"
      @close="closeCalender" @panel-change="onPanelChange" :formatter="formatter" switch-mode="month"
      class="fixed_height" />

    <van-popup v-model:show="showFilter" position="bottom" type="dialog" round closeable
      :style="{ bottom: 'calc(56px + env(safe-area-inset-bottom))' }" safe-area-inset-bottom>
      <div class="filter-panel">
        <div class="filter-title">筛选</div>
        <div class="filter-container">
          <div class="filter-tabs">
            <div class="tab-item" :class="{ active: activeTab === 'student' }" @click="activeTab = 'student'">学生</div>
            <div class="tab-item" :class="{ active: activeTab === 'teacher' }" @click="activeTab = 'teacher'">教师</div>
          </div>

          <div class="filter-content">
            <div v-if="activeTab === 'student'" class="search-group">
              <van-field v-model="studentSearch" placeholder="请输入学生姓名" left-icon="search"
                @keyup.enter="confirmFilter" />
              <div v-if="studentSearchResults.length > 0" class="search-results">
                <div v-for="student in studentSearchResults" :key="student.studentId" class="result-item"
                  :class="{ selected: filterConditions.students.includes(student.studentId) }"
                  @click="toggleStudent(student)">
                  {{ student.studentName }}
                </div>
              </div>
              <div v-if="selectedStudents.length > 0" class="selected-items">
                <div v-for="student in selectedStudents" :key="student.studentId" class="selected-item">
                  {{ student.studentName }}
                  <van-icon name="cross" @click.stop="removeStudent(student.studentId)" />
                </div>
              </div>
            </div>

            <div v-if="activeTab === 'teacher'" class="search-group">
              <van-field v-model="teacherSearch" placeholder="请输入教师姓名" left-icon="search"
                @keyup.enter="confirmFilter" />
              <div v-if="teacherSearchResults.length > 0" class="search-results">
                <div v-for="teacher in teacherSearchResults" :key="teacher.userId" class="result-item"
                  :class="{ selected: filterConditions.teachers.includes(teacher.userId) }"
                  @click="toggleTeacher(teacher)">
                  {{ teacher.nickName }}
                </div>
              </div>
              <div v-if="selectedTeachers.length > 0" class="selected-items">
                <div v-for="teacher in selectedTeachers" :key="teacher.userId" class="selected-item">
                  {{ teacher.nickName }}
                  <van-icon name="cross" @click.stop="removeTeacher(teacher.userId)" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="filter-footer">
          <van-button type="default" @click="resetFilter">重置</van-button>
          <van-button type="primary" @click="confirmFilter">确定</van-button>
        </div>
      </div>
    </van-popup>

    <div class="card-list">
      <div v-for="(item, idx) in schdulePlans" :key="idx" class="course-card" :class="{ completed: item.status === 2 }">
        <div class="card-left">
          <div class="time-badge">
            <span class="time">{{ item.classStartTime }}</span>
            <span class="time-divider">-</span>
            <span class="time">{{ item.classEndTime }}</span>
          </div>
          <div class="status-tag" :class="getStatusClass(item.status)">
            {{ getStatusText(item.status) }}
          </div>
        </div>
        <div class="card-content">
          <div class="course-name">{{ item.courseName }}</div>
          <div class="teacher-tag">
            <van-icon name="user-o" size="12" />
            <span>{{ item.teacherName }}</span>
          </div>
          <div class="student-row">
            <van-icon :name="item.status === 1 ? 'user-o' : 'check-circle-o'" size="12" />
            <span class="student-names">{{ item.status === 1 ? item.studentNames : item.signStudentNames }}</span>
          </div>
        </div>
        <div class="card-right">
          <van-button v-if="item.status === 1" type="primary" size="small" round
            @click.stop="toAddAttendance(item.schedulePlanId)">
            {{ new Date() > parseDate(item.end) ? '去考勤' : '编辑' }}
          </van-button>
          <van-button v-if="item.status === 2" type="danger" size="small" round
            @click.stop="toEditAttendance(item.schedulePlanId)">修改</van-button>
        </div>
      </div>
    </div>

    <div class="add-schedule-button">
      <van-button type="primary" @click="goAddSchedule()" block>
        + 新增排课
      </van-button>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getAttendanceDates, getSchedulePlanList, searchStudents } from '@/api/schedule';
import { debounce, parseDate } from '@/utils/common';
import { getTeacherList } from '@/api/user';
import { ensureWecomLogin } from '@/utils/wecomAuth';

const route = useRoute();
const router = useRouter();

const showCalendar = ref(false);
const showFilter = ref(false);
const activeTab = ref('student');
const schdulePlans = ref([]);
const date = ref(null);
const studentSearch = ref('');
const teacherSearch = ref('');
const studentSearchResults = ref([]);
const teacherSearchResults = ref([]);
const allTeachers = ref([]);
const filterConditions = reactive({
  students: [],
  teachers: []
});
const courseDates = ref([]);

const selectedStudents = computed(() => filterConditions.students);
const selectedTeachers = computed(() => filterConditions.teachers);

watch(studentSearch, debounce((newVal) => {
  if (newVal.trim()) {
    searchStudentsApi(newVal);
  } else {
    studentSearchResults.value = [];
  }
}, 300));

watch(teacherSearch, debounce((newVal) => {
  if (newVal.trim()) {
    filterTeachers(newVal);
  } else {
    teacherSearchResults.value = allTeachers.value;
  }
}, 300));

function searchStudentsApi(keyword) {
  searchStudents({ studentName: keyword }).then(res => {
    studentSearchResults.value = res;
  });
}

function filterTeachers(keyword) {
  const filtered = allTeachers.value.filter(teacher =>
    teacher.nickName && teacher.nickName.includes(keyword)
  );
  teacherSearchResults.value = filtered;
}

function getAttendanceDate(params) {
  getAttendanceDates(params).then(res => {
    courseDates.value = res;
  });
}

function loadAllTeachers() {
  getTeacherList().then(res => {
    allTeachers.value = res;
    teacherSearchResults.value = res;
  });
}

function getSchedulePlans() {
  const params = {
    startDate: date.value,
    endDate: date.value,
    studentIds: filterConditions.students.map(s => s.studentId),
    teacherIds: filterConditions.teachers.map(t => t.userId)
  };
  getSchedulePlanList(params).then(res => {
    schdulePlans.value = res;
  });
}

function toggleStudent(student) {
  const list = filterConditions.students;
  const index = list.findIndex(s => s.id === student.studentId);
  if (index === -1) {
    list.push(student);
  } else {
    list.splice(index, 1);
  }
  studentSearch.value = '';
  studentSearchResults.value = [];
}

function toggleTeacher(teacher) {
  const list = filterConditions.teachers;
  const index = list.findIndex(t => t.id === teacher.userId);
  if (index === -1) {
    list.push(teacher);
  } else {
    list.splice(index, 1);
  }
  teacherSearch.value = '';
  teacherSearchResults.value = allTeachers.value;
}

function removeStudent(studentId) {
  const list = filterConditions.students;
  const index = list.findIndex(s => s.studentId === studentId);
  if (index !== -1) {
    list.splice(index, 1);
  }
}

function removeTeacher(teacherId) {
  const list = filterConditions.teachers;
  const index = list.findIndex(t => t.userId === teacherId);
  if (index !== -1) {
    list.splice(index, 1);
  }
}

function resetFilter() {
  studentSearch.value = '';
  teacherSearch.value = '';
  studentSearchResults.value = [];
  teacherSearchResults.value = allTeachers.value;
  filterConditions.students = [];
  filterConditions.teachers = [];
}

function confirmFilter() {
  showFilter.value = false;
  getSchedulePlans();
}

function closeCalender() {
  showCalendar.value = false;
}

function onPanelChange(dateObj) {
  getAttendanceDate({
    date: formatDate(dateObj.date)
  });
}

function onSelectDate(selectedDate) {
  date.value = formatDate(selectedDate);
  getSchedulePlans();
  closeCalender();
}

function formatDate(dateObj) {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatter(day) {
  if (courseDates.value.includes(formatDate(day.date))) {
    day.bottomInfo = '●';
  }
  return day;
}

function goAddSchedule() {
  router.push({ path: '/addSchedule' });
}

function toEditAttendance(schedulePlanId) {
  router.push({
    path: '/editAttendance',
    query: { schedulePlanId }
  });
}

function toAddAttendance(schedulePlanId) {
  router.push({
    path: '/addAttendance',
    query: { schedulePlanId }
  });
}

async function initData() {
  if (route.query.state) {
    date.value = route.query.state;
  } else if (route.query.date) {
    date.value = route.query.date;
  } else {
    const today = new Date();
    date.value = formatDate(today);
  }
  await ensureWecomLogin(route.query.code);
  getAttendanceDate();
  loadAllTeachers();
  getSchedulePlans();
}

function getStatusText(status) {
  return status === 1 ? '未考勤' : '已完成';
}

function getStatusClass(status) {
  return status === 1 ? 'status-pending' : 'status-completed';
}

onMounted(() => {
  initData();
});
</script>

<style scoped>
.time-table-page {
  background-color: #f5f7fa;
  min-height: 100%;
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: #fff;
  border-bottom: 1px solid #f0f0f0;
}

.filter-header .van-icon {
  color: #1677ff;
  font-size: 20px;
}

.filter-panel {
  padding: 20px;
  background-color: #f8f8f8;
  min-height: 400px;
}

.filter-title {
  font-size: 18px;
  margin-bottom: 20px;
  color: #333;
  font-weight: 600;
}

.filter-container {
  display: flex;
  height: 300px;
}

.filter-tabs {
  width: 100px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-right: 20px;
}

.tab-item {
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  color: #666;
  background-color: #fff;
  text-align: center;
  transition: all 0.2s;
}

.tab-item.active {
  color: #1677ff;
  font-weight: 600;
  background-color: rgba(22, 119, 255, 0.1);
}

.filter-content {
  flex: 1;
  background-color: #fff;
  border-radius: 12px;
  padding: 16px;
  overflow-y: auto;
}

.search-group {
  margin-bottom: 16px;
}

.search-results {
  max-height: 150px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  margin-top: 12px;
}

.result-item {
  padding: 12px 16px;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
  font-size: 14px;
}

.result-item:last-child {
  border-bottom: none;
}

.result-item:hover {
  background-color: #fafafa;
}

.result-item.selected {
  background-color: rgba(22, 119, 255, 0.08);
  color: #1677ff;
}

.selected-items {
  margin-top: 16px;
}

.selected-item {
  display: inline-flex;
  align-items: center;
  background-color: rgba(22, 119, 255, 0.1);
  color: #1677ff;
  padding: 4px 10px;
  border-radius: 12px;
  margin-right: 8px;
  margin-bottom: 8px;
  font-size: 12px;
}

.selected-item .van-icon {
  margin-left: 4px;
  cursor: pointer;
  font-size: 12px;
}

.filter-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  padding: 0 5%;
}

.filter-footer .van-button {
  flex: 1;
  margin: 0 10px;
  height: 44px;
  font-size: 16px;
  border-radius: 8px;
}

.card-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  padding-bottom: 70px;
}

.course-card {
  display: flex;
  align-items: stretch;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
}

.course-card.completed {
  opacity: 0.85;
}

.course-card:active {
  transform: scale(0.98);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.card-left {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px 10px;
  background: linear-gradient(180deg, #f0f7ff 0%, #e6f0ff 100%);
  min-width: 80px;
  gap: 6px;
}

.time-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.time {
  font-size: 13px;
  font-weight: 600;
  color: #1677ff;
}

.time-divider {
  font-size: 10px;
  color: #91caff;
}

.status-tag {
  font-size: 10px;
  padding: 2px 10px;
  border-radius: 10px;
  font-weight: 500;
  white-space: nowrap;
}

.status-pending {
  background-color: rgba(255, 140, 0, 0.08);
  color: #ff8c00;
}

.status-completed {
  background-color: rgba(0, 200, 83, 0.08);
  color: #00c853;
}

.card-content {
  flex: 1;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.course-name {
  font-size: 15px;
  font-weight: 500;
  color: #262626;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.teacher-tag {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #8c8c8c;
}

.teacher-tag .van-icon {
  color: #999;
}

.student-row {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #8c8c8c;
}

.student-row .van-icon {
  color: #999;
}

.student-names {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-right {
  display: flex;
  align-items: center;
  padding-right: 12px;
}

.card-right .van-button {
  font-size: 13px;
}

.add-schedule-button {
  position: fixed;
  bottom: calc(56px + env(safe-area-inset-bottom));
  left: 16px;
  right: 16px;
  z-index: 100;
}

.add-schedule-button .van-button {
  height: 44px;
  border-radius: 12px;
  font-size: 16px;
}
</style>